// The promise list's customer filter (spec 03 §4.9): a typeahead over module
// 09's customer book, behind its door for the reader, as the product picker
// is over module 01's products.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { usePromiseCustomerSearch } from "../hooks/promise-queries";

export interface PickedCustomer {
  readonly id: string;
  readonly display_name: string;
  readonly customer_no: string;
}

export function PromiseCustomerPicker(props: {
  readonly label: string;
  readonly onPick: (customer: PickedCustomer) => void;
  readonly "data-testid"?: string;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const q = useDebouncedValue(text.trim(), 250);
  const search = usePromiseCustomerSearch(q.length >= 2 ? q : "");
  const testId = props["data-testid"] ?? "planning-promise-customer-picker";
  const items = search.data ?? [];
  return (
    <div className="planning-picker" data-testid={testId}>
      <Field label={props.label} wide>
        <input
          type="search"
          value={text}
          placeholder={t("planning.promises.customerPicker.placeholder")}
          data-testid={`${testId}-input`}
          onChange={(e) => {
            setText(e.target.value);
          }}
        />
      </Field>
      {q.length >= 2 ? (
        <ul
          className="planning-picker-list"
          aria-label={t("planning.promises.customerPicker.results")}
        >
          {search.isPending ? (
            <li className="planning-hint">{t("planning.promises.customerPicker.searching")}</li>
          ) : null}
          {search.error !== null ? (
            <li className="planning-hint">{t("planning.promises.customerPicker.error")}</li>
          ) : null}
          {search.data !== undefined && items.length === 0 ? (
            <li className="planning-hint">{t("planning.promises.customerPicker.none")}</li>
          ) : null}
          {items.map((c) => (
            <li key={c.id}>
              <Button
                size="sm"
                data-testid={`${testId}-option`}
                onClick={() => {
                  props.onPick(c);
                  setText("");
                }}
              >
                <span className="planning-sku">{c.customer_no}</span> {c.display_name}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
